import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import Icon from '@components/icons/Icon';
import styles from '@features/cms/components/categories/CategoryLogoCarouselWithTitle/CategoryLogoCarouselWithTitle.styles';
import { ICategoryLogoCarousel, TLogoItemType } from '@features/cms/typescript';
import Heading from '@components/Heading/Heading';
import theme from '@styles/theme';
import sanitizeHtml from '@utils/sanitizeHtml';
import prepareURLByCMSLink from '@utils/prepareURLByCMSLink';
import ResponsiveImage from '@components/ResponsiveImage/ResponsiveImage';
type PropsType = {
  data: ICategoryLogoCarousel;
  defaultBackground?: string;
};
const CategoryLogoCarouselWithTitle = ({
  data,
  defaultBackground = theme.rawColors.neutralWhite
}: PropsType) => {
  const {
    code,
    background,
    marginTop,
    marginBottom,
    logos,
    title
  } = data;
  const [animate, setAnimate] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const innerWrapperRef = useRef<HTMLDivElement>(null);
  const sectionTitleAlt = useMemo(() => title ? title.replace(/<[^>]*>/g, '').trim() : '', [title]);
  const renderLogo = (item: TLogoItemType, keySuffix: string) => item.link ? <Link sx={styles.icon} href={prepareURLByCMSLink(item.link)} key={`${item.id}-${keySuffix}`} data-testid={`CategoryLogoCarouselWithTitle-logo-${item.id}-${keySuffix}`}>
        {item?.svg?.path ? <ResponsiveImage alt={item?.svg?.metadata?.alt || sectionTitleAlt} image={item.svg.path} sx={styles.icon} width={70} height={40} /> : <Icon icon={item.icon} />}
      </Link> : <div key={`${item.id}-${keySuffix}`}>
        {item?.svg?.path ? <ResponsiveImage alt={item?.svg?.metadata?.alt || sectionTitleAlt} image={item.svg.path} width={70} height={40} sx={styles.icon} /> : <Icon addSx={styles.icon} icon={item.icon} />}
      </div>;
  const onResize = () => {
    setAnimate(true);
    setTimeout(() => {
      if (wrapperRef.current && innerWrapperRef.current) {
        const containerWidth = wrapperRef.current.clientWidth;
        const iconsWrapperWidth = innerWrapperRef.current.clientWidth;
        if (iconsWrapperWidth > containerWidth) {
          setAnimate(true);
        } else {
          setAnimate(false);
        }
      }
    }, 1000);
  };
  useEffect(() => {
    onResize();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);
  return <section id={code} sx={styles.container(background || defaultBackground, marginTop, marginBottom)} data-testid={code}>
      {title && <Heading headingType={4} h={4} mobile pb={40} dangerouslySetInnerHTML={sanitizeHtml(title)} />}
      <div ref={wrapperRef} sx={styles.wrapper(animate)} data-testid="CategoryLogoCarouselWithTitle-wrapper">
        <div sx={styles.innerWrapper(animate)}>
          <div ref={innerWrapperRef} sx={styles.logoStrip(animate)} data-testid="CategoryLogoCarouselWithTitle-logoStrip">
            {logos?.map(item => renderLogo(item, 'a'))}
          </div>
          <div sx={styles.logoStripClone(animate)} data-testid="CategoryLogoCarouselWithTitle-logoStripClone">
            {logos?.map(item => renderLogo(item, 'b'))}
          </div>
        </div>
      </div>
    </section>;
};
export default CategoryLogoCarouselWithTitle;